import { useMutation, useQuery } from '@tanstack/react-query';
import { apiClient } from '@/services/apiClient';
import { Routes } from './routes';
import { SUBSCRIPTION_SUMMARY } from '@/constants/queries';
import type {
  FinalizeSubscriptionRequest,
  FinalizeSubscriptionResponse,
  GetSubscriptionResponse,
  SubscriptionsSummary,
} from '@/types/subscription';
import type { PriceBreakdown } from '@/types/checkout';

/**
 * Subscription checkout summary — plan, pricing, customer, and the Chargebee
 * payment intent + publishable keys. Unlike `useCheckoutData`, errors are NOT
 * swallowed: a failed summary (e.g. DUPLICATE_SUBSCRIPTION) must surface.
 */
export const useSubscriptionSummary = ({ enableFetch }: { enableFetch: boolean }) => {
  return useQuery({
    queryKey: [SUBSCRIPTION_SUMMARY],
    enabled: enableFetch,
    queryFn: async (): Promise<SubscriptionsSummary> => {
      return await apiClient.get<SubscriptionsSummary>(Routes.SUBSCRIPTION_SUMMARY);
    },
  });
};

export const useFinalizeSubscription = () => {
  return useMutation({
    mutationFn: async (request: FinalizeSubscriptionRequest) => {
      return await apiClient.post<FinalizeSubscriptionResponse>(
        Routes.SUBSCRIPTION_FINALIZE,
        request as unknown as Record<string, unknown>
      );
    },
  });
};

export const useCloseSubscription = () => {
  return useMutation({
    mutationFn: async () => {
      return await apiClient.post(Routes.SUBSCRIPTION_CLOSE);
    },
  });
};

interface SubscriptionTaxResponse {
  isValid: boolean;
  priceBreakdown: PriceBreakdown;
  zipCode: string;
  state?: string;
}

export const useSubscriptionTax = () => {
  return useMutation({
    mutationFn: async ({ zipCode }: { zipCode: string }) => {
      return await apiClient.get<SubscriptionTaxResponse>(`${Routes.SUBSCRIPTION_TAX}?zipCode=${zipCode}`);
    },
  });
};

/** Plain fetcher (not a hook) — consumed by the polling loop. */
export const getSubscriptionStatus = async (subscriptionId: string): Promise<GetSubscriptionResponse> => {
  return await apiClient.get<GetSubscriptionResponse>(`${Routes.SUBSCRIPTION_SUMMARY}/${subscriptionId}`);
};
